/**
 * 業務ルールの画面（`/rules/$ruleId`）— 問いは「この業務ルールはどう判断するか」。入った直後に見えるのは
 * 業務ルール名 ／ タブ ／ ルール（入力 ・ 出力と中身の表）だけにする（Dhik 2026-09-29「本当に出すべき情報を
 * 1 or 2 個に」、業務ルールの画面は名指しで 1 つ）。
 *
 * **タブは ルール ／ 使われている場所。** 既定は ルール。ルールのタブは入力 ・ 出力を上に、中身の表をその下に
 * 1 枚で描く（Dhik 2026-09-30「業務ルールの入力・出力と中身を統合してルールにして」。前は 中身 と 入力 ・ 出力 が
 * 別のタブで、何を読んで何を出すかと、どう判断するかを行き来しないと読めなかった）。並びは業務ロジックの
 * モーダル（`RuleSections`）と同じ入力 → 中身。読まれる先と使っている業務は依存を追う人の深掘りなので、既定の
 * タブから外す。「使われている場所」の名前は業務詳細のタブに揃え、件数は業務詳細と同じくタブの横に出す。
 * 形の札 ・ 案件の札 ・ 節の見出し「基本」「中身」は出さない（形は表を、案件での扱いは左右の地色と空札を
 * 見れば分かる）。
 *
 * **キーの決め方はルールのタブの中で畳んでおく。** 見出し「キーの決め方 N 個 ▸」を押すと開き、もう一度押すと閉じる。
 *
 * **案件を選んでいるときは、タブの中身を左に変更前、右に変更後で並べる。** 「変更前と比較」はタブの並びの
 * 右端に置き、`compare=0` なら変更後 1 面。案件がこの業務ルールを変えていなければ 1 面で、比較のボタンは
 * 押せない（その理由「この案件で変わっていない」はボタンを指すと出る）。キーの決め方はキーごとに左右を
 * 1 段に揃え、片側に無いキーは反対側を斜線の空札にする。取得は 1 本で、master と案件適用後の姿が同じ応答で届く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール 1 件の入力・出力と中身を既定のルールのタブに 1 枚で描き、使われている場所（読まれる先・使っている業務）をタブへ分け、案件を選んでいれば変更前と変更後を左右に並べる。
 * @graph-connects useRule [calls] 業務ルール 1 件の master と案件適用後の姿の取得
 * @graph-connects DetailTabs [calls] ルール ／ 使われている場所 のタブ
 * @graph-connects RuleIoFields [calls] 入力と出力
 * @graph-connects RuleBodyContent [calls] 中身 4 種
 * @graph-connects SortDeriveList [calls] 並べ替えのキーの決め方
 * @graph-connects RuleReaders [calls] 読まれる先
 * @graph-connects RuleUsedBy [calls] 使っている業務
 * @graph-connects DiffSplitPanes [calls] 変更前と変更後の左右比較
 * @graph-connects DeriveFoldHeading [calls] キーの決め方の畳みの見出し
 */
import type { ElementRef, RuleView } from '@cortex/domain-spec-console';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailSection } from '../components/spec/DetailSection.js';
import { DetailTabs, type DetailTab } from '../components/spec/DetailTabs.js';
import {
  DiffAbsentCell,
  DiffCompareToggle,
  DiffSplitPanes,
  type DiffSide,
  type DiffSplitRow,
} from '../components/spec/DiffSplitPanes.js';
import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { NodeTypeBadge } from '../components/spec/NodeTypeBadge.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { toElementId } from '../hooks/queries/spec-graph-element-term-queries.js';
import { useRule } from '../hooks/queries/spec-graph-rule-flow-queries.js';
import { isNotFoundError } from '../lib/orpc-errors.js';
import { isPjMode } from '../lib/pj-mode.js';
import { useCompareSearch } from './flow-workspace/use-compare-search.js';
import { pjLinkSearch } from './pj-link.js';
import { pjTextOf } from './pj-search.js';
import { BUSINESS_MAP_ROUTE, RULE_ROUTE } from './route-paths.js';
import { untranslatedTitle } from './rule-body-parts.js';
import {
  RuleBodyContent,
  ruleChangeState,
  RuleIoFields,
  RuleReaders,
  RuleUsedBy,
} from './rule-detail-sections.js';
import {
  alignDeriveKeys,
  DeriveAbsentCard,
  deriveMarkOf,
  SortDeriveEntry,
  SortDeriveList,
  sortDeriveOf,
  type SortDeriveView,
} from './rule-sort-body.js';

/**
 * 業務ルールの画面を、取得中・未存在・失敗・本体の 4 状態で描く。
 *
 * @returns 業務ルールの画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの画面を、取得中・未存在・失敗・本体の 4 状態で描く。
 * @graph-connects useRule [calls] 業務ルール 1 件の取得
 * @graph-connects pjLinkSearch [calls] 戻るリンクへ案件を引き継ぐ検索条件
 */
export function RulePage() {
  const { t } = useTranslation();
  const { ruleId } = useParams({ from: RULE_ROUTE });
  const pj = pjTextOf(useSearch({ from: RULE_ROUTE }).pj);
  const id = toElementId(ruleId);
  const pjNumber = isPjMode(pj) ? toElementId(pj) : undefined;
  const query = useRule(id ?? 0, pjNumber);

  const backLink = (
    <Link
      to={BUSINESS_MAP_ROUTE}
      {...pjLinkSearch(pj)}
      className="text-sm text-[var(--color-accent-primary)] underline"
    >
      {t('spec.backToList')}
    </Link>
  );

  if (id === undefined || isNotFoundError(query.error)) {
    return <NotFoundState id={ruleId} action={backLink} />;
  }
  if (query.error !== null && query.error !== undefined) {
    return <ErrorState error={query.error} />;
  }
  if (query.isLoading || query.data === undefined) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="4rem" />
        <Skeleton variant="rectangular" height="16rem" />
      </div>
    );
  }

  const { before, after } = query.data;
  const shown = after ?? before;
  if (shown === null) return <NotFoundState id={ruleId} action={backLink} />;

  // 業務ルールと案件で key を付け、別の業務ルールへ移ったらキーの決め方の開閉を既定（閉じる）へ戻す。
  // 付けないと、キャッシュ済みの業務ルールへ戻ったとき骨を経ずに同じ部品が再利用され、開いたまま入る
  return (
    <RuleBody
      key={`${String(shown.ruleId)}:${String(pjNumber ?? '')}`}
      shown={shown}
      before={pjNumber === undefined ? null : before}
      after={after}
      pjNumber={pjNumber}
    />
  );
}

/** `RuleBody` の props。 @graph-connects none */
interface RuleBodyProps {
  /** 見出しに使う姿（変更後。案件で外したなら変更前）。 */
  shown: RuleView;
  /** 変更前の姿。案件を選んでいない・案件で足したなら null。 */
  before: RuleView | null;
  /** 変更後の姿。案件で外したなら null。 */
  after: RuleView | null;
  /** 重ねている案件番号。選んでいなければ undefined。 */
  pjNumber: number | undefined;
}

/** 業務ルールの画面のタブ。 @graph-connects none */
type RuleTab = 'rule' | 'usage';

/** 面 1 つの描き方。業務ルール・描いている面・反対側の姿を受けて中身を返す。 @graph-connects none */
type RulePaneRender = (
  rule: RuleView,
  side: DiffSide | undefined,
  counterpart: RuleView | null,
) => ReactNode;

/**
 * 案件が変えた業務ルールの、ルールのタブの中に変更があるか。中身と、入力の用語と出力で比べる。使われている
 * 場所はほかの業務ルールの増減だけで動くので印を付けない（`ruleSignature` と同じ理由）。
 *
 * @param before 変更前の姿。案件で足したなら null
 * @param after 変更後の姿。案件で外したなら null
 * @returns ルールのタブに変更があるか
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が変えた業務ルールの変更前と変更後を比べ、ルールのタブ（入力・出力と中身）に変更があるかを返す。
 * @graph-connects none
 */
export function ruleTabChanged(before: RuleView | null, after: RuleView | null): boolean {
  if (before === null || after === null) return true;
  const ruleOf = (rule: RuleView) =>
    JSON.stringify([rule.inputs.map((input) => input.term), rule.output, rule.body]);
  return ruleOf(before) !== ruleOf(after);
}

/**
 * 取得済みの業務ルールを、見出し ・ タブ ・ 選んでいるタブの中身へ配る。
 *
 * @param props 見出しの姿・変更前・変更後・案件番号
 * @returns 業務ルールの本体
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 取得済みの業務ルールを見出しとタブへ配り、選んでいるタブの中身を描き、案件を選んでいれば変更前と変更後を左右に並べる。
 * @graph-connects ruleChangeState [calls] 業務ルールの変更前と変更後の突き合わせ
 * @graph-connects ruleTabChanged [calls] ルールのタブの変更の印
 * @graph-connects NodeTypeBadge [calls] 見出しの左上の型の札
 * @graph-connects useCompareSearch [calls] 左右比較の開閉
 */
function RuleBody({ shown, before, after, pjNumber }: RuleBodyProps) {
  const { t } = useTranslation();
  const pjMode = pjNumber !== undefined;
  const state = pjMode ? ruleChangeState(before, after) : 'unchanged';
  /* 変わっていない業務ルールを左右に並べても、同じ中身が 2 つ出るだけになる。 */
  const compare = useCompareSearch(state !== 'unchanged');
  const faded = pjMode && state === 'unchanged' ? 'opacity-60' : '';
  const [tab, setTab] = useState<RuleTab>('rule');
  const [deriveOpen, setDeriveOpen] = useState(false);
  const deriveFold: DeriveFold = {
    open: deriveOpen,
    onToggle: () => {
      setDeriveOpen((open) => !open);
    },
  };

  const single = after ?? shown;
  const split = pjMode && compare.split;
  /* 1 面で、案件がこの業務ルールを外した。変更後の中身は無い。 */
  const removed = pjMode && !compare.split && after === null;
  const changed = pjMode && state !== 'unchanged' && ruleTabChanged(before, after);
  const tabs: readonly DetailTab<RuleTab>[] = [
    { id: 'rule', label: t('businessLogic.ruleTabs.rule'), changed },
    {
      id: 'usage',
      label: t('businessLogic.ruleTabs.usage'),
      count: single.readBy.length + single.usedByBusinesses.length,
    },
  ];

  const compareOf = (side: DiffSide | undefined, counterpart: RuleView | null) =>
    side === undefined ? {} : { side, counterpart };
  // 入力 ・ 出力を上、中身の表を下に 1 枚で描く。罫線 1 本で「何を読んで何を出すか」と「どう判断するか」を分ける
  const ruleOf: RulePaneRender = (rule, side, counterpart) => (
    <Card padding="md">
      <div className="flex flex-col gap-3">
        <RuleIoFields rule={rule} pjNumber={pjNumber} {...compareOf(side, counterpart)} />
        <div className="border-t border-[var(--color-border)] pt-3">
          <RuleBodyContent body={rule.body} {...compareOf(side, counterpart)} />
        </div>
      </div>
    </Card>
  );
  const readersOf: RulePaneRender = (rule) => (
    <DetailSection title={t('businessLogic.readByTitle')}>
      <RuleReaders rule={rule} pjNumber={pjNumber} />
    </DetailSection>
  );
  const cellOf = (render: RulePaneRender, side: DiffSide) => {
    const rule = side === 'before' ? before : after;
    const counterpart = side === 'before' ? after : before;
    if (rule === null) return <DiffAbsentCell state={side === 'before' ? 'created' : 'deleted'} />;
    // min-w-0: 列の多い表が面の幅を押し広げて反対側へはみ出さないようにする
    return <div className={`min-w-0 ${faded}`}>{render(rule, side, counterpart)}</div>;
  };
  const splitOf = (key: string, render: RulePaneRender, extra: DiffSplitRow[] = []) => (
    <DiffSplitPanes
      pjNumber={pjNumber === undefined ? null : String(pjNumber)}
      rows={[{ key, before: cellOf(render, 'before'), after: cellOf(render, 'after') }, ...extra]}
    />
  );

  const singleDerive = sortDeriveOf(single);
  const ruleTab = split ? (
    splitOf('rule', ruleOf, deriveSplitRows(before, after, faded, deriveFold))
  ) : removed ? (
    <DiffAbsentCell state="deleted" />
  ) : (
    <>
      {ruleOf(single, undefined, null)}
      {singleDerive.length > 0 && (
        <section aria-label={t('businessLogic.deriveTitle')} className="flex flex-col gap-3 px-1">
          <DeriveFoldHeading count={singleDerive.length} fold={deriveFold} />
          {deriveFold.open && (
            <Card padding="md">
              <SortDeriveList derive={singleDerive} />
            </Card>
          )}
        </section>
      )}
    </>
  );
  // 読まれる先 ｜ 使っている業務。短いリンクの一覧なので 1 面では横に並べる
  const usageTab = (
    <div className={`grid gap-3${split || removed ? '' : ' lg:grid-cols-2'}`}>
      {split ? splitOf('readers', readersOf) : removed ? null : readersOf(single, undefined, null)}
      <DetailSection title={t('businessLogic.usedByTitle')}>
        <RuleUsedBy rule={single} pjNumber={pjNumber} />
      </DetailSection>
    </div>
  );

  const compareToggle = (
    <span
      title={state === 'unchanged' ? t('businessLogic.unchangedInPj') : undefined}
      data-compare-note={state}
    >
      <DiffCompareToggle
        split={compare.split}
        disabled={compare.disabled}
        onToggle={compare.toggle}
      />
    </span>
  );

  return (
    <div className="flex min-h-0 flex-col gap-3 overflow-y-auto">
      <header className="flex flex-wrap items-center gap-2" data-id={shown.ruleId}>
        <NodeTypeBadge nodeType="Rule" />
        <h1
          className="text-2xl font-bold text-[var(--color-text-primary)]"
          title={untranslatedTitle(shown.label, shown.untranslated, t)}
        >
          {displayLabel(shown.label, t, shown.untranslated)}
        </h1>
      </header>
      <DetailTabs
        tabs={tabs}
        value={tab}
        onChange={setTab}
        {...(pjMode ? { trailing: compareToggle } : {})}
      />
      <div className="flex min-w-0 flex-col gap-3" data-rule-tab={tab}>
        {tab === 'rule' ? ruleTab : usageTab}
      </div>
    </div>
  );
}

/**
 * キーの決め方 1 つの左右の片側。業務ルールがその側に無ければ空、キーの決め方がその側に無ければ
 * 斜線の空札（反対側で足した ／ 外した）、あれば枠つきで描く。
 *
 * @param entry その側のキーの決め方
 * @param keyRef キー
 * @param side 描いている面
 * @param rule その側の業務ルール
 * @param counterpart 反対側の業務ルール
 * @param faded 変わっていない業務ルールを薄くする class
 * @returns 片側のセル
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business キーの決め方 1 つの片側を、無い・空札・中身の 3 通りで描く。
 * @graph-connects SortDeriveEntry [calls] キー 1 つの決め方
 * @graph-connects DeriveAbsentCard [calls] 片側に無いキーの空札
 */
function deriveCell(
  entry: SortDeriveView | null,
  keyRef: ElementRef,
  side: DiffSide,
  rule: RuleView | null,
  counterpart: RuleView | null,
  faded: string,
): ReactNode {
  if (rule === null) return <div />;
  if (entry === null) {
    return <DeriveAbsentCard keyRef={keyRef} state={side === 'before' ? 'created' : 'deleted'} />;
  }
  return (
    // min-w-0: 列の多い判定表が枠ごと反対側の面へはみ出さず、判定表の横スクロールに収まる
    <Card padding="md" className={`min-w-0 ${faded}`}>
      <SortDeriveEntry
        derive={entry}
        side={side}
        mark={deriveMarkOf(keyRef.id, side, counterpart)}
      />
    </Card>
  );
}

/**
 * 案件モードのキーの決め方の段。畳みの見出しの段 1 つと、開いているときだけキーで揃えたキーごとの段。
 * どちらの面にもキーの決め方が無ければ段を作らない。見出しは左右で 1 つにし、変更前の面に置く
 * （案件で足した業務ルールなら変更後の面）。個数はキーで揃えた後の段の数。
 *
 * @param before 変更前の業務ルール
 * @param after 変更後の業務ルール
 * @param faded 変わっていない業務ルールを薄くする class
 * @param fold 開いているかと開閉の口
 * @returns 左右比較の段
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後のキーの決め方をキーで揃え、見出しとキーごとの左右の段にする。
 * @graph-connects alignDeriveKeys [calls] キーで左右を揃える
 * @graph-connects deriveCell [calls] 片側のセル
 * @graph-connects DeriveFoldHeading [calls] 畳みの見出し
 */
function deriveSplitRows(
  before: RuleView | null,
  after: RuleView | null,
  faded: string,
  fold: DeriveFold,
): DiffSplitRow[] {
  const pairs = alignDeriveKeys(sortDeriveOf(before), sortDeriveOf(after));
  if (pairs.length === 0) return [];
  const heading = <DeriveFoldHeading count={pairs.length} fold={fold} />;
  const headOnBefore = before !== null;
  return [
    {
      key: 'derive',
      before: headOnBefore ? heading : <div />,
      after: headOnBefore ? <div /> : heading,
    },
    ...(fold.open ? pairs : []).map((pair) => ({
      // 順が入れ替わったキーは片側だけの段が 2 つ出るので、どちら側にあるかも鍵に入れる
      key: `derive-${pair.before === null ? 'after' : pair.after === null ? 'before' : 'both'}-${String(pair.key.id)}`,
      before: deriveCell(pair.before, pair.key, 'before', before, after, faded),
      after: deriveCell(pair.after, pair.key, 'after', after, before, faded),
    })),
  ];
}

/**
 * キーの決め方の畳み。開いているかと、開閉の口。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの画面のキーの決め方が開いているかと、それを切り替える口
 * @graph-connects none
 */
interface DeriveFold {
  /** 開いているか。 */
  readonly open: boolean;
  /** 開閉を切り替える。 */
  readonly onToggle: () => void;
}

/**
 * キーの決め方の見出し「キーの決め方 N 個 ▸」。押すと開き、もう一度押すと閉じる。
 *
 * @param props キーの決め方の個数と畳み
 * @returns 押せる見出し
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business キーの決め方の節の見出しに個数と開閉の印を出し、押されたら開閉を切り替える。
 * @graph-connects none
 */
function DeriveFoldHeading({ count, fold }: { count: number; fold: DeriveFold }) {
  const { t } = useTranslation();
  return (
    <h2 className="text-base font-bold text-[var(--color-text-primary)]">
      <button
        type="button"
        aria-expanded={fold.open}
        data-derive-fold={fold.open ? 'open' : 'closed'}
        onClick={fold.onToggle}
        className="inline-flex items-baseline gap-2 rounded-sm text-left hover:underline"
      >
        {t('businessLogic.deriveTitle')}
        <span className="text-xs font-normal text-[var(--color-text-muted)]">
          {t('businessLogic.deriveCount', { count })}
        </span>
        <span aria-hidden="true">{fold.open ? '▾' : '▸'}</span>
      </button>
    </h2>
  );
}
